import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

// Register eCharts components
echarts.use([TooltipComponent, GridComponent, BarChart, CanvasRenderer]);

// Interface for ML Bias Data
export interface BiasMLDataItem {
  Attribute: string;
  DisparityRatio: number;
  CalculatedImpact?: number;
  BiasDetected?: boolean;
  ExternalCompliance?: string;
  InternalPolicyCompliance?: string;
  Risk?: string;
  Action?: string;
  BiasDrift?: string;
  IntersectionalBias?: string;
}

interface BiasMLChartProps {
  data: BiasMLDataItem[];
  title: string;
  description: string;
}

const BiasMLChart: React.FC<BiasMLChartProps> = ({
  data,
  title,
  description
}) => {
  const { getThemeColor } = useAppContext();

  // Utility to safely get theme color with fallback
  const getSafeThemeColor = (key: string, fallback: string) => {
    const color = getThemeColor?.(key);
    return color || fallback;
  };

  const primaryColor = getSafeThemeColor('primary', '#007bff');
  const borderColor = getSafeThemeColor('border-color', '#ddd');
  const tooltipBg = getSafeThemeColor('body-highlight-bg', '#ffffff');
  const tooltipText = getSafeThemeColor('light-text-emphasis', '#000');
  const axisLabelColor = getSafeThemeColor('secondary-color', '#666');

  const disparityRatios = data.map(item => item.DisparityRatio);
  const attributes = data.map(item => item.Attribute);

  const options = {
    color: primaryColor,
    tooltip: {
      trigger: 'axis',
      backgroundColor: tooltipBg,
      borderColor: borderColor,
      textStyle: { color: tooltipText }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '5%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: attributes,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: axisLabelColor }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: axisLabelColor }
    },
    series: [
      {
        type: 'bar',
        data: disparityRatios,
        itemStyle: { color: primaryColor }
      }
    ]
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <h4>{title}</h4>
        <p>{description}</p>
        <ReactEChartsCore
          echarts={echarts}
          option={options}
          style={{ height: '400px' }}
        />
      </Card.Body>
    </Card>
  );
};

export default BiasMLChart;
